import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Download, Loader2, Map, Mountain, Route, WifiOff } from 'lucide-react';
import { MapBounds } from '../../types';
import { getAllOfflineAreas } from '../../services/tileDownloaderService';
import { boundsOverlapRatio } from '../../utils/boundsUtils';
import { useTranslation } from 'react-i18next';

interface FollowPreflightModalProps {
  mode: 'route_follow' | 'free_drive_follow';
  title: string;
  bounds: MapBounds | null;
  is3DPreferred: boolean;
  dataReady: boolean;
  dataLoading: boolean;
  dataStatus: string | null;
  onDownloadData: () => void;
  onOpenOfflineMaps: () => void;
  onDownloadOfflineMaps?: (bounds: MapBounds) => Promise<{ tileCount: number } | null>;
  onContinue: () => void;
  onClose: () => void;
}

const FollowPreflightModal: React.FC<FollowPreflightModalProps> = ({
  mode,
  title,
  bounds,
  is3DPreferred,
  dataReady,
  dataLoading,
  dataStatus,
  onDownloadData,
  onOpenOfflineMaps,
  onDownloadOfflineMaps,
  onContinue,
  onClose,
}) => {
  const { t } = useTranslation();
  const boundsReady = !!bounds;

  const [mapDownloading, setMapDownloading] = useState(false);
  const [mapDownloaded, setMapDownloaded] = useState(false);
  const [mapDownloadStatus, setMapDownloadStatus] = useState<string | null>(null);

  // Auto-detect if offline maps already cover this area
  useEffect(() => {
    if (!bounds) return;
    try {
      const existing = getAllOfflineAreas().filter(a => a.status === 'complete');
      if (existing.some(a => boundsOverlapRatio(a.bounds, bounds) > 0.9)) {
        setMapDownloaded(true);
        setMapDownloadStatus('Kartet er allerede lagret for dette området.');
      }
    } catch { /* ignore */ }
  }, [bounds]);

  const handleDownloadMaps = async () => {
    if (!bounds) return;

    if (onDownloadOfflineMaps) {
      setMapDownloading(true);
      setMapDownloadStatus('Laster ned kartfliser...');
      try {
        const result = await onDownloadOfflineMaps(bounds);
        if (result) {
          setMapDownloaded(true);
          setMapDownloadStatus(`${result.tileCount} kartfliser lagret.`);
        } else {
          setMapDownloadStatus('Kartet er allerede lagret for dette området.');
          setMapDownloaded(true);
        }
      } catch {
        setMapDownloadStatus('Kunne ikke laste ned kart.');
      } finally {
        setMapDownloading(false);
      }
    } else {
      // Fallback: open offline manager
      onOpenOfflineMaps();
    }
  };

  return (
    <div className="fixed inset-0 z-[5200] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-3xl border border-white/15 bg-[var(--bg-surface)] shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[var(--border-primary)]">
          <div className="flex items-center gap-2 text-2xs font-black uppercase tracking-widest text-amber-400">
            <AlertTriangle size={14} />
            {t("auto.routes.followpreflightmodal.for_du_kjorer", "Før du kjører")}
          </div>
          <div className="mt-2 text-[18px] font-black text-[var(--text-primary)] leading-tight">
            {mode === 'route_follow' ? title : 'Klargjør kjørevisning'}
          </div>
          <div className="mt-2 text-sm text-[var(--text-secondary)]">
            {mode === 'route_follow'
              ? 'Last gjerne ned kart og relevante punkter før navigasjon. Det gir bedre stabilitet ved svak dekning.'
              : 'GPS-følging fungerer best når kart og punkter er klare lokalt før du begynner å kjøre.'}
          </div>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="rounded-2xl border border-[var(--border-primary)] bg-[var(--bg-surface-alt)] px-4 py-3">
            <div className="flex items-center gap-2 text-tiny font-black uppercase tracking-widest text-[var(--text-secondary)]">
              <Map size={13} />
              {t("auto.routes.followpreflightmodal.anbefalt_omrade", "Anbefalt område")}
            </div>
            <div className="mt-2 text-sm text-[var(--text-primary)]">
              {boundsReady ? 'Nåværende navigasjonsområde er klart for nedlasting.' : 'Kunne ikke beregne område enda.'}
            </div>
            <div className="mt-2 flex items-center gap-2 text-tiny text-[var(--text-secondary)]">
              <Route size={12} />
              {mode === 'route_follow' ? 'Langs ruten med buffer' : 'Rundt gjeldende kartutsnitt'}
            </div>
            <div className="mt-1 flex items-center gap-2 text-tiny text-[var(--text-secondary)]">
              {is3DPreferred ? <Mountain size={12} /> : <Map size={12} />}
              {is3DPreferred ? '3D valgt: last gjerne ned både terrengkart og flyfoto' : '2D valgt: terrengkart holder vanligvis'}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={handleDownloadMaps}
              disabled={!boundsReady || mapDownloading}
              className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-4 text-left disabled:opacity-50"
            >
              <div className="flex items-center gap-2 text-emerald-300 font-black text-tiny uppercase tracking-widest">
                {mapDownloading ? <Loader2 size={14} className="animate-spin" /> : mapDownloaded ? <CheckCircle2 size={14} /> : <WifiOff size={14} />}
                Last ned kart
              </div>
              <div className="mt-2 text-sm text-[var(--text-primary)]">
                {mapDownloading
                  ? (mapDownloadStatus || 'Laster ned kartfliser...')
                  : mapDownloaded
                    ? (mapDownloadStatus || 'Kart er lagret for dette området.')
                    : 'Åpner offline-kart med dette området forhåndsvalgt.'}
              </div>
            </button>

            <button
              onClick={onDownloadData}
              disabled={!boundsReady || dataLoading}
              className="rounded-2xl border border-sky-500/30 bg-sky-500/10 px-4 py-4 text-left disabled:opacity-50"
            >
              <div className="flex items-center gap-2 text-sky-300 font-black text-tiny uppercase tracking-widest">
                {dataLoading ? <Loader2 size={14} className="animate-spin" /> : dataReady ? <CheckCircle2 size={14} /> : <Download size={14} />}
                Lagre punkter og ruter
              </div>
              <div className="mt-2 text-sm text-[var(--text-primary)]">
                {dataLoading
                  ? (dataStatus || 'Laster lokale data...')
                  : dataReady
                    ? 'Punkter og ruter er lagret lokalt for dette området.'
                    : 'Prehenter relevante punkter, ruter og GPX-data.'}
              </div>
            </button>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-[var(--border-primary)] flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-2xl bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] text-tiny font-black uppercase tracking-widest"
          >
            {t("auto.routes.followpreflightmodal.avbryt", "Avbryt")}
          </button>
          <button
            onClick={onContinue}
            className="px-5 py-2.5 rounded-2xl bg-emerald-600 text-white text-tiny font-black uppercase tracking-widest"
          >
            {t("auto.routes.followpreflightmodal.fortsett", "Fortsett")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FollowPreflightModal;
